import type { CurrentSession } from '../models/current-session';
import type { Session } from './session';

const STORAGE_KEY = 'ipodariger.portal.session';

/**
 * One place holds the session, exactly as WebAdmin's `SessionStore` does.
 *
 * **Nothing here is a credential.** It used to hold an access token and a refresh token, kept in
 * `localStorage` so a reload did not sign the customer out — a deliberate trade against XSS that
 * this change removes rather than mitigates. What is left is the display copy of the account, so
 * the portal shell paints on reload without waiting for a round trip; the session itself is an
 * `HttpOnly` cookie, and the server decides on every request whether it is still one.
 *
 * A stale copy here therefore grants nothing. The worst it can do is show the shell for a moment
 * before the first request comes back 401 and clears it.
 */
let current: Session | null = read();

const listeners = new Set<() => void>();

function read(): Session | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === null ? null : (JSON.parse(stored) as Session);
    } catch {
        // A private window, cleared site data, or a half-written value: treat it as signed out
        // rather than letting the whole app fail to start.
        return null;
    }
}

function emit(): void {
    for (const listener of listeners) {
        listener();
    }
}

export function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Stable reference between changes, so `useSyncExternalStore` does not loop. */
export function getSession(): Session | null {
    return current;
}

export function setSession(result: CurrentSession): void {
    current = {
        user: result.user,
        expiresAt: result.expiresAt
    };

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
    } catch {
        // Storage can be unavailable or full. The in-memory session still works for this tab.
    }

    emit();
}

export function clearSession(): void {
    if (current === null) {
        return;
    }

    current = null;

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to do — the in-memory session is already gone.
    }

    emit();
}

/**
 * What the store holds where there is no browser: nobody.
 *
 * The content pages are rendered at build time, so their markup necessarily shows a signed-out
 * header. React reads this during hydration and the real snapshot immediately after, which is what
 * makes the swap a re-render rather than a mismatch that throws the server's markup away.
 */
export function getServerSession(): Session | null {
    return null;
}
